import { defineNuxtPlugin, useRuntimeConfig } from '#imports';

/**
 * `nuxt-gtag` is configured with `initMode: 'manual'`, so the gtag.js script is
 * never added to the document by the module. This plugin injects it lazily —
 * after the first user interaction, or when the main thread goes idle — so the
 * analytics JS and its long tasks stay out of the initial interactivity window.
 * Events fired via `useTrackEvent` before this point are buffered in
 * `window.dataLayer` (set up by nuxt-gtag) and flush on load.
 */
export default defineNuxtPlugin({
  name: 'gtag-lazy',
  parallel: true,
  setup() {
    const { id, url } = useRuntimeConfig().public.gtag as {
      id?: string;
      url?: string;
    };

    if (!id) return;

    const controller = new AbortController();
    let loaded = false;

    function load() {
      if (loaded || document.querySelector('script[data-gtag]')) return;
      loaded = true;
      controller.abort();

      const script = document.createElement('script');
      script.async = true;
      script.src = `${
        url || 'https://www.googletagmanager.com/gtag/js'
      }?id=${id}`;
      script.dataset.gtag = '';
      document.head.append(script);
    }

    const interactionEvents = [
      'pointerdown',
      'keydown',
      'touchstart',
      'scroll',
    ];
    interactionEvents.forEach((event) =>
      window.addEventListener(event, load, {
        passive: true,
        signal: controller.signal,
      })
    );

    // Fallback for users who never interact (still need bounce/pageview data):
    // load once the browser is idle, capped a few seconds out.
    const onIdle =
      typeof requestIdleCallback === 'undefined'
        ? (callback: () => void) => setTimeout(callback, 3000)
        : (callback: () => void) =>
            requestIdleCallback(callback, { timeout: 5000 });
    onIdle(load);
  },
});
